/**
 * Top-level module navigation — the single source of truth for the portal's
 * sections. As new modules ship (or a /preview concept graduates), add them
 * here once and they appear in BOTH the desktop topbar and the mobile burger
 * drawer.
 *
 * Two groups:
 *   - NAV_SECTIONS     — live modules (Home, YouTube, Salience).
 *   - PREVIEW_SECTIONS — future-module concept mockups (/preview/*), rendered
 *     under a "Coming soon" heading with a muted "soon" tag so they're
 *     discoverable from the nav but never mistaken for live surfaces.
 *
 * Layout split:
 *   - Desktop (md+): SectionNav renders horizontally in the TopBar.
 *   - Mobile (<md):  the topbar links are hidden; SectionNav is folded into the
 *     burger drawer instead (MobileNav on full-width pages; injected at the top
 *     of the module sidebars elsewhere).
 */
import { Link, useLocation } from "react-router-dom";
import { LogOut, Settings, X } from "lucide-react";
import { signOut, useSession } from "../lib/auth";

interface Section {
  to: string;
  label: string;
  isActive: (pathname: string) => boolean;
}

export const NAV_SECTIONS: Section[] = [
  { to: "/", label: "Home", isActive: (p) => p === "/" },
  { to: "/youtube", label: "YouTube", isActive: (p) => p.startsWith("/youtube") },
  {
    to: "/salience",
    label: "Salience",
    isActive: (p) => p.startsWith("/salience"),
  },
];

/** Future-module concept previews — not live. */
export const PREVIEW_SECTIONS: Section[] = [
  {
    to: "/preview/discord",
    label: "Discord Monitor",
    isActive: (p) => p === "/preview/discord",
  },
  {
    to: "/preview/inbox",
    label: "Inbox Triage",
    isActive: (p) => p === "/preview/inbox",
  },
];

function SectionLink({
  section,
  active,
  vertical,
  preview,
  onNavigate,
}: {
  section: Section;
  active: boolean;
  vertical: boolean;
  preview?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={section.to}
      onClick={onNavigate}
      className={`flex items-center gap-1.5 rounded uppercase tracking-wider transition-colors ${
        vertical ? "px-3 py-2 text-sm" : "px-2 py-1"
      } ${
        active
          ? "bg-brand-indigo/10 text-brand-indigo"
          : "text-ink-muted hover:bg-black/5 hover:text-ink"
      }`}
    >
      <span className={vertical ? "" : "whitespace-nowrap"}>{section.label}</span>
      {preview && (
        <span className="rounded bg-agenda-elite/15 px-1 py-px text-[8px] font-bold tracking-wider text-agenda-elite">
          Soon
        </span>
      )}
    </Link>
  );
}

export function SectionNav({
  orientation = "horizontal",
  onNavigate,
  className = "",
}: {
  orientation?: "horizontal" | "vertical";
  /** Called after a link is tapped — used to close the mobile drawer. */
  onNavigate?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  const user = useSession().data?.user;
  const vertical = orientation === "vertical";

  if (vertical) {
    return (
      <div className={`flex flex-col gap-1 text-[12px] font-semibold ${className}`}>
        {NAV_SECTIONS.map((s) => (
          <SectionLink
            key={s.to}
            section={s}
            active={s.isActive(pathname)}
            vertical
            onNavigate={onNavigate}
          />
        ))}
        <div className="mt-2 px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted/70">
          Coming soon
        </div>
        {PREVIEW_SECTIONS.map((s) => (
          <SectionLink
            key={s.to}
            section={s}
            active={s.isActive(pathname)}
            vertical
            preview
            onNavigate={onNavigate}
          />
        ))}
        {/* Account controls � the TopBar hides these below sm to make room. */}
        {user && (
          <div className="mt-2 border-t border-rule pt-2 sm:hidden">
            <div
              className="truncate px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted/70"
              title={user.email}
            >
              {user.name}
            </div>
            {user.roles.includes("admin") && (
              <Link
                to="/settings/users"
                onClick={onNavigate}
                className="flex items-center gap-2 rounded px-3 py-2 text-sm uppercase tracking-wider text-ink-muted transition-colors hover:bg-black/5 hover:text-ink"
              >
                <Settings size={14} /> Settings
              </Link>
            )}
            <button
              onClick={() => void signOut()}
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm uppercase tracking-wider text-ink-muted transition-colors hover:bg-black/5 hover:text-ink"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  // Horizontal (desktop topbar): live sections, a divider, then previews.
  return (
    <nav className={`flex items-center gap-0.5 text-[12px] font-semibold ${className}`}>
      {NAV_SECTIONS.map((s) => (
        <SectionLink
          key={s.to}
          section={s}
          active={s.isActive(pathname)}
          vertical={false}
          onNavigate={onNavigate}
        />
      ))}
      <span className="mx-1 h-4 w-px bg-rule" aria-hidden />
      {PREVIEW_SECTIONS.map((s) => (
        <SectionLink
          key={s.to}
          section={s}
          active={s.isActive(pathname)}
          vertical={false}
          preview
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}

/**
 * Mobile-only burger drawer for full-width pages (home, concept previews) that
 * have no module sidebar of their own. Mirrors the sidebar drawer chrome so the
 * burger behaves identically everywhere. Hidden on md+.
 */
export function MobileNav({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {open && (
        <div
          className="fixed inset-0 top-14 z-20 bg-black/40 md:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={`
          fixed top-14 bottom-0 left-0 z-30 flex w-64 flex-col overflow-hidden border-r border-rule bg-surface
          transition-transform duration-200 ease-out md:hidden
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex items-center justify-between border-b border-rule px-3 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Sections
          </span>
          <button
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-black/5 hover:text-ink"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-2">
          <SectionNav orientation="vertical" onNavigate={onClose} />
        </div>
      </aside>
    </>
  );
}
